// SupportTicketsPage — администрирование тикетов поддержки.
//
// Двухпанельная master-detail верстка:
//   - слева: список тикетов с фильтрами по статусу и приоритету;
//   - справа: открытый тикет с историей сообщений (chat-like) и формой ответа +
//     дропдаунами «изменить статус» / «приоритет».
//
// Авто-refresh раз в 30 секунд. Кнопка «Обновить» — для немедленного refetch.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LifeBuoy,
  Loader2,
  RefreshCw,
  Send,
  Flag,
  AlertCircle,
  CheckCircle2,
  MessageSquare,
  XCircle,
  Sparkles,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  adminSupportAPI,
  AdminSupportTicket,
  AdminSupportTicketMessage,
  SupportTicketPriority,
  SupportTicketStatus,
} from '../services/api';

// ─── helpers ─────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: { value: SupportTicketStatus | ''; label: string }[] = [
  { value: '', label: 'Все' },
  { value: 'open', label: 'Открытые' },
  { value: 'user_replied', label: 'Ответ юзера' },
  { value: 'answered', label: 'Отвечено' },
  { value: 'closed', label: 'Закрытые' },
];

const STATUS_LABEL: Record<SupportTicketStatus, string> = {
  open: 'Открыт',
  user_replied: 'Ответ юзера',
  answered: 'Отвечено',
  closed: 'Закрыт',
};

const STATUS_BADGE: Record<SupportTicketStatus, string> = {
  open: 'bg-amber-100 text-amber-800',
  user_replied: 'bg-blue-100 text-blue-800',
  answered: 'bg-green-100 text-green-800',
  closed: 'bg-gray-200 text-gray-700',
};

const PRIORITY_LABEL: Record<SupportTicketPriority, string> = {
  low: 'Низкий',
  normal: 'Обычный',
  high: 'Высокий',
};

const PRIORITY_BADGE: Record<SupportTicketPriority, string> = {
  low: 'bg-gray-100 text-gray-600',
  normal: 'bg-blue-50 text-blue-700',
  high: 'bg-red-100 text-red-700',
};

const PRIORITY_FILTER_OPTIONS: { value: SupportTicketPriority | ''; label: string }[] = [
  { value: '', label: 'Все приоритеты' },
  { value: 'high', label: 'Высокий' },
  { value: 'normal', label: 'Обычный' },
  { value: 'low', label: 'Низкий' },
];

function formatDt(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// Активные статусы — те, по которым админу есть смысл отвечать.
// Они выбраны как стартовый фильтр.
const DEFAULT_FILTER_STATUS: SupportTicketStatus | '' = 'open';

// REFRESH_INTERVAL_MS — авто-обновление списка и активного тикета.
const REFRESH_INTERVAL_MS = 30_000;

// ─── component ───────────────────────────────────────────────────────────────

const SupportTicketsPage: React.FC = () => {
  const [tickets, setTickets] = useState<AdminSupportTicket[]>([]);
  const [filterStatus, setFilterStatus] = useState<SupportTicketStatus | ''>(DEFAULT_FILTER_STATUS);
  const [filterPriority, setFilterPriority] = useState<SupportTicketPriority | ''>('');
  const [loadingList, setLoadingList] = useState(true);

  const [activeId, setActiveId] = useState<number | null>(null);
  const [activeTicket, setActiveTicket] = useState<AdminSupportTicket | null>(null);
  const [activeMessages, setActiveMessages] = useState<AdminSupportTicketMessage[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [replyBody, setReplyBody] = useState('');
  const [replyCloseAfter, setReplyCloseAfter] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [drafting, setDrafting] = useState(false);

  const [patching, setPatching] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // ── Loading list ──────────────────────────────────────────────────────────

  const loadList = useCallback(
    async (silent = false) => {
      if (!silent) setLoadingList(true);
      try {
        const r = await adminSupportAPI.listTickets(filterStatus || undefined);
        if (r.success) setTickets(r.tickets || []);
      } catch (e: unknown) {
        if (!silent) {
          toast.error(e instanceof Error ? e.message : 'Не удалось загрузить тикеты');
        }
      } finally {
        if (!silent) setLoadingList(false);
      }
    },
    [filterStatus],
  );

  const loadDetail = useCallback(
    async (id: number, silent = false) => {
      if (!silent) setLoadingDetail(true);
      try {
        const r = await adminSupportAPI.getTicket(id);
        if (r.success) {
          setActiveTicket(r.ticket);
          setActiveMessages(r.messages || []);
        }
      } catch (e: unknown) {
        if (!silent) {
          toast.error(e instanceof Error ? e.message : 'Не удалось загрузить тикет');
        }
      } finally {
        if (!silent) setLoadingDetail(false);
      }
    },
    [],
  );

  // initial + on-filter load
  useEffect(() => {
    void loadList(false);
  }, [loadList]);

  // when activeId changes — load detail
  useEffect(() => {
    if (activeId !== null) {
      void loadDetail(activeId, false);
    } else {
      setActiveTicket(null);
      setActiveMessages([]);
    }
  }, [activeId, loadDetail]);

  // auto-refresh
  useEffect(() => {
    const t = setInterval(() => {
      void loadList(true);
      if (activeId !== null) void loadDetail(activeId, true);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [activeId, loadList, loadDetail]);

  // scroll to bottom of messages on new
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [activeMessages.length, activeId]);

  // ── Filtered list (client-side priority filter) ──────────────────────────

  const filteredTickets = useMemo(() => {
    if (!filterPriority) return tickets;
    return tickets.filter((t) => t.priority === filterPriority);
  }, [tickets, filterPriority]);

  // ── Actions ──────────────────────────────────────────────────────────────

  const handleSubmitReply = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!activeId || submitting) return;
      const body = replyBody.trim();
      if (!body) {
        toast.error('Пустое сообщение');
        return;
      }
      setSubmitting(true);
      try {
        const r = await adminSupportAPI.addMessage(activeId, body, replyCloseAfter);
        if (!r.success) {
          toast.error(r.message || 'Не удалось отправить ответ');
          return;
        }
        toast.success(replyCloseAfter ? 'Ответ отправлен, тикет закрыт' : 'Ответ отправлен');
        setReplyBody('');
        setReplyCloseAfter(false);
        await loadDetail(activeId, true);
        await loadList(true);
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : 'Ошибка отправки');
      } finally {
        setSubmitting(false);
      }
    },
    [activeId, submitting, replyBody, replyCloseAfter, loadDetail, loadList],
  );

  // handleDraftReply — попросить LLM подготовить черновик ответа по контексту
  // тикета и положить его в форму. Оператор затем правит и жмёт «Отправить».
  const handleDraftReply = useCallback(async () => {
    if (!activeId || drafting) return;
    setDrafting(true);
    try {
      const r = await adminSupportAPI.draftReply(activeId);
      if (!r.success || !r.draft) {
        toast.error(r.message || 'Не удалось подготовить ответ');
        return;
      }
      setReplyBody(r.draft);
      toast.success('Черновик готов — проверьте и отправьте');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Ошибка генерации');
    } finally {
      setDrafting(false);
    }
  }, [activeId, drafting]);

  const handleChangeStatus = useCallback(
    async (status: SupportTicketStatus) => {
      if (!activeId || !activeTicket || patching) return;
      if (activeTicket.status === status) return;
      setPatching(true);
      try {
        const r = await adminSupportAPI.patchTicket(activeId, { status });
        if (!r.success) {
          toast.error(r.message || 'Не удалось изменить статус');
          return;
        }
        toast.success('Статус обновлён');
        await loadDetail(activeId, true);
        await loadList(true);
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : 'Ошибка');
      } finally {
        setPatching(false);
      }
    },
    [activeId, activeTicket, patching, loadDetail, loadList],
  );

  const handleChangePriority = useCallback(
    async (priority: SupportTicketPriority) => {
      if (!activeId || !activeTicket || patching) return;
      if (activeTicket.priority === priority) return;
      setPatching(true);
      try {
        const r = await adminSupportAPI.patchTicket(activeId, { priority });
        if (!r.success) {
          toast.error(r.message || 'Не удалось изменить приоритет');
          return;
        }
        toast.success('Приоритет обновлён');
        await loadDetail(activeId, true);
        await loadList(true);
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : 'Ошибка');
      } finally {
        setPatching(false);
      }
    },
    [activeId, activeTicket, patching, loadDetail, loadList],
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <LifeBuoy className="h-8 w-8 text-blue-600" />
            Поддержка
          </h1>
          <p className="text-gray-600 mt-1">
            Обращения пользователей. Активный фильтр + ручное обновление; авто-refresh раз в 30 сек.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            void loadList(false);
            if (activeId !== null) void loadDetail(activeId, false);
          }}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          Обновить
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_1fr]">
        {/* ── LIST ────────────────────────────────────────────────────────── */}
        <div className="border border-gray-200 rounded-lg bg-white overflow-hidden flex flex-col">
          <div className="px-3 py-2 border-b border-gray-100 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as SupportTicketStatus | '')}
                className="flex-1 text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value as SupportTicketPriority | '')}
                className="flex-1 text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white"
              >
                {PRIORITY_FILTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-xs text-gray-500">
              Всего: {filteredTickets.length}
              {filterPriority && tickets.length !== filteredTickets.length && (
                <span className="ml-1 text-gray-400">из {tickets.length}</span>
              )}
            </div>
          </div>

          <div className="overflow-auto max-h-[calc(100vh-15rem)]">
            {loadingList ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            ) : filteredTickets.length === 0 ? (
              <p className="p-4 text-gray-500 text-sm">Нет тикетов</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {filteredTickets.map((t) => {
                  const isActive = t.id === activeId;
                  return (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => setActiveId(t.id)}
                        className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 ${
                          isActive ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[t.status]}`}>
                            {STATUS_LABEL[t.status]}
                          </span>
                          {t.priority !== 'normal' && (
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${PRIORITY_BADGE[t.priority]}`}>
                              <Flag className="h-3 w-3" />
                              {PRIORITY_LABEL[t.priority]}
                            </span>
                          )}
                          <span className="ml-auto text-xs text-gray-500 whitespace-nowrap">
                            {formatDt(t.lastMessageAt)}
                          </span>
                        </div>
                        <div className="font-medium text-gray-900 text-sm truncate" title={t.subject}>
                          {t.subject || '(без темы)'}
                        </div>
                        <div className="text-xs text-gray-500 truncate" title={t.userEmail || `user_id=${t.userId}`}>
                          {t.userEmail || `user_id=${t.userId}`}
                          {typeof t.messageCount === 'number' && (
                            <span className="ml-2 inline-flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" /> {t.messageCount}
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* ── DETAIL ─────────────────────────────────────────────────────── */}
        <div className="border border-gray-200 rounded-lg bg-white overflow-hidden flex flex-col min-h-[36rem]">
          {!activeId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8">
              <LifeBuoy className="h-10 w-10 mb-3" />
              <p className="text-sm">Выберите тикет в списке слева</p>
            </div>
          ) : loadingDetail && !activeTicket ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : !activeTicket ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8">
              <AlertCircle className="h-10 w-10 mb-3" />
              <p className="text-sm">Тикет не найден</p>
            </div>
          ) : (
            <>
              {/* header */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <h2 className="font-semibold text-gray-900 text-lg truncate" title={activeTicket.subject}>
                      #{activeTicket.id} · {activeTicket.subject || '(без темы)'}
                    </h2>
                    <div className="text-sm text-gray-500 mt-0.5 truncate">
                      {activeTicket.userEmail || `user_id=${activeTicket.userId}`}
                      {activeTicket.userName && <span className="ml-2 text-gray-700">{activeTicket.userName}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${STATUS_BADGE[activeTicket.status]}`}>
                      {STATUS_LABEL[activeTicket.status]}
                    </span>
                    <select
                      value={activeTicket.status}
                      onChange={(e) => handleChangeStatus(e.target.value as SupportTicketStatus)}
                      disabled={patching}
                      className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white disabled:opacity-60"
                    >
                      <option value="open">Открыт</option>
                      <option value="answered">Отвечено</option>
                      <option value="user_replied">Ответ юзера</option>
                      <option value="closed">Закрыт</option>
                    </select>
                    <select
                      value={activeTicket.priority}
                      onChange={(e) => handleChangePriority(e.target.value as SupportTicketPriority)}
                      disabled={patching}
                      className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white disabled:opacity-60"
                    >
                      <option value="low">Низкий</option>
                      <option value="normal">Обычный</option>
                      <option value="high">Высокий</option>
                    </select>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-400 flex items-center gap-3 flex-wrap">
                  <span>Создан: {formatDt(activeTicket.createdAt)}</span>
                  <span>Последнее: {formatDt(activeTicket.lastMessageAt)}</span>
                  {activeTicket.closedAt && <span>Закрыт: {formatDt(activeTicket.closedAt)}</span>}
                </div>
              </div>

              {/* messages */}
              <div className="flex-1 overflow-auto px-4 py-3 space-y-3 bg-gray-50">
                {activeMessages.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">Сообщений пока нет</p>
                ) : (
                  activeMessages.map((m) => {
                    if (m.authorRole === 'system') {
                      return (
                        <div key={m.id} className="text-center">
                          <span className="inline-block text-xs italic text-gray-500 bg-white border border-gray-200 rounded-full px-3 py-1">
                            {m.body}
                          </span>
                        </div>
                      );
                    }
                    const isAdmin = m.authorRole === 'admin';
                    return (
                      <div
                        key={m.id}
                        className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-3 py-2 shadow-sm border ${
                            isAdmin
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-800 border-gray-200'
                          }`}
                        >
                          <div className={`text-xs mb-1 flex items-center gap-2 ${isAdmin ? 'text-blue-100' : 'text-gray-500'}`}>
                            {isAdmin ? (
                              <>
                                <CheckCircle2 className="h-3 w-3" /> Админ
                              </>
                            ) : (
                              <>
                                <MessageSquare className="h-3 w-3" /> Пользователь
                              </>
                            )}
                            <span>· {formatDt(m.createdAt)}</span>
                          </div>
                          <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                            {m.body}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* reply form */}
              {activeTicket.status === 'closed' ? (
                <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 text-sm text-gray-500 flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Тикет закрыт. Чтобы продолжить, измените статус выше.
                </div>
              ) : (
                <form
                  onSubmit={handleSubmitReply}
                  className="px-4 py-3 border-t border-gray-100 bg-white space-y-2"
                >
                  <textarea
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Ответ пользователю…"
                    rows={3}
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                    disabled={submitting}
                  />
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={replyCloseAfter}
                        onChange={(e) => setReplyCloseAfter(e.target.checked)}
                        disabled={submitting}
                      />
                      Закрыть тикет после отправки
                    </label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={handleDraftReply}
                        disabled={drafting || submitting}
                        title="Сгенерировать черновик ответа с учётом контекста тикета. Перед отправкой можно отредактировать."
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {drafting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Sparkles className="h-4 w-4" />
                        )}
                        Подготовить ответ
                      </button>
                      <button
                        type="submit"
                        disabled={submitting || !replyBody.trim()}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        Отправить
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportTicketsPage;
